export const getBasePrice = (product = {}) => {
  const price = Number(product.price || 0);
  return Number.isFinite(price) && price > 0 ? price : 0;
};

const clampSalePercentage = (value) => {
  const percentage = Number(value);
  return Number.isFinite(percentage) ? Math.min(100, Math.max(0, percentage)) : 0;
};

export const calculateSalePriceFromPercentage = ({ price, salePercentage }) => {
  const basePrice = getBasePrice({ price });
  const percentage = clampSalePercentage(salePercentage);

  if (!basePrice || percentage <= 0) {
    return 0;
  }

  return Number((basePrice * (1 - (percentage / 100))).toFixed(2));
};

export const calculateSalePercentageFromPrice = ({ price, salePrice }) => {
  const basePrice = getBasePrice({ price });
  const targetSalePrice = Number(salePrice);

  if (!basePrice || !Number.isFinite(targetSalePrice) || targetSalePrice < 0 || targetSalePrice >= basePrice) {
    return 0;
  }

  return Number((((basePrice - targetSalePrice) / basePrice) * 100).toFixed(2));
};

export const formatPricingInputValue = (value) => (
  Number.isFinite(value) ? String(Number(value.toFixed(2))) : ''
);

const hasInputValue = (value) => String(value ?? '').trim() !== '';

export const syncSalePricingFields = (current, name, value) => {
  const next = { [name]: value };
  const price = name === 'price' ? value : current.price;
  const salePercentage = name === 'salePercentage' ? value : current.salePercentage;
  const salePrice = name === 'salePrice' ? value : current.salePrice;

  if (name === 'salePercentage') {
    next.salePrice = hasInputValue(value) && Number(value) > 0
      ? formatPricingInputValue(calculateSalePriceFromPercentage({ price, salePercentage: value }))
      : '';
  }

  if (name === 'salePrice') {
    next.salePercentage = hasInputValue(value)
      ? formatPricingInputValue(calculateSalePercentageFromPrice({ price, salePrice: value }))
      : '';
  }

  if (name === 'price') {
    if (hasInputValue(salePrice)) {
      next.salePercentage = formatPricingInputValue(calculateSalePercentageFromPrice({ price, salePrice }));
    } else if (hasInputValue(salePercentage) && Number(salePercentage) > 0) {
      next.salePrice = formatPricingInputValue(calculateSalePriceFromPercentage({ price, salePercentage }));
    }
  }

  return next;
};

export const getSalePrice = (product = {}) => {
  const explicitSalePrice = Number(product.salePrice || 0);
  if (Number.isFinite(explicitSalePrice) && explicitSalePrice > 0) {
    return explicitSalePrice;
  }

  return calculateSalePriceFromPercentage({
    price: product.price,
    salePercentage: product.salePercentage,
  });
};

export const getSalePercentage = (product = {}) => {
  const explicitPercentage = Number(product.salePercentage || 0);
  if (Number.isFinite(explicitPercentage) && explicitPercentage > 0) {
    return explicitPercentage;
  }

  const price = getBasePrice(product);
  const salePrice = getSalePrice(product);

  if (!price || !salePrice || salePrice >= price) {
    return 0;
  }

  return calculateSalePercentageFromPrice({ price, salePrice });
};

export const getDisplayPrice = (product = {}) => {
  const salePrice = getSalePrice(product);
  return salePrice > 0 ? salePrice : getBasePrice(product);
};

export const productHasSale = (product = {}) => {
  const price = getBasePrice(product);
  const salePrice = getSalePrice(product);

  return price > 0 && salePrice > 0 && salePrice < price;
};
